"use client";

import { useEffect, useState } from "react";
import { analiticaActivaEnNavegador } from '@/lib/analytics/entorno';
import { LOCALE_COOKIE, type Locale } from '@/lib/i18n';

type GlobalErrorStrings = {
  connectionTitle: string;
  errorTitle: string;
  connectionDesc: string;
  errorDesc: string;
  tryThese: string;
  useBrowser: string;
  clearCache: string;
  checkInternet: string;
  updateBrowser: string;
  tryAgain: string;
};

const GLOBAL_ERROR_T: Record<Locale, GlobalErrorStrings> = {
  en: {
    connectionTitle: 'Connection Error',
    errorTitle: 'Something went wrong',
    connectionDesc: 'There was a problem connecting to the server. This might be a browser compatibility issue.',
    errorDesc: 'An unexpected error occurred. Please try again.',
    tryThese: 'Try one of these:',
    useBrowser: 'Use Chrome or Safari instead',
    clearCache: 'Clear your browser cache',
    checkInternet: 'Check your internet connection',
    updateBrowser: 'Update your browser to the latest version',
    tryAgain: 'Try Again',
  },
  es: {
    connectionTitle: 'Error de conexión',
    errorTitle: 'Algo salió mal',
    connectionDesc: 'Hubo un problema al conectar con el servidor. Puede ser un problema de compatibilidad del navegador.',
    errorDesc: 'Ocurrió un error inesperado. Inténtalo de nuevo.',
    tryThese: 'Intenta una de estas opciones:',
    useBrowser: 'Usa Chrome o Safari en su lugar',
    clearCache: 'Limpia la caché del navegador',
    checkInternet: 'Verifica tu conexión a internet',
    updateBrowser: 'Actualiza tu navegador a la última versión',
    tryAgain: 'Intentar de nuevo',
  },
  pt: {
    connectionTitle: 'Erro de conexão',
    errorTitle: 'Algo deu errado',
    connectionDesc: 'Houve um problema ao conectar com o servidor. Pode ser um problema de compatibilidade do navegador.',
    errorDesc: 'Ocorreu um erro inesperado. Tente novamente.',
    tryThese: 'Tente uma destas opções:',
    useBrowser: 'Use Chrome ou Safari',
    clearCache: 'Limpe o cache do navegador',
    checkInternet: 'Verifique sua conexão com a internet',
    updateBrowser: 'Atualize seu navegador para a versão mais recente',
    tryAgain: 'Tentar novamente',
  },
  de: {
    connectionTitle: 'Verbindungsfehler',
    errorTitle: 'Etwas ist schiefgelaufen',
    connectionDesc: 'Bei der Verbindung mit dem Server ist ein Problem aufgetreten. Möglicherweise liegt es an der Kompatibilität Ihres Browsers.',
    errorDesc: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    tryThese: 'Versuchen Sie Folgendes:',
    useBrowser: 'Verwenden Sie stattdessen Chrome oder Safari',
    clearCache: 'Leeren Sie den Browser-Cache',
    checkInternet: 'Prüfen Sie Ihre Internetverbindung',
    updateBrowser: 'Aktualisieren Sie Ihren Browser auf die neueste Version',
    tryAgain: 'Erneut versuchen',
  },
  fr: {
    connectionTitle: 'Erreur de connexion',
    errorTitle: 'Une erreur est survenue',
    connectionDesc: 'Un problème est survenu lors de la connexion au serveur. Il peut s’agir d’un problème de compatibilité du navigateur.',
    errorDesc: 'Une erreur inattendue s’est produite. Veuillez réessayer.',
    tryThese: 'Essayez l’une de ces solutions :',
    useBrowser: 'Utilisez plutôt Chrome ou Safari',
    clearCache: 'Videz le cache de votre navigateur',
    checkInternet: 'Vérifiez votre connexion Internet',
    updateBrowser: 'Mettez votre navigateur à jour vers la dernière version',
    tryAgain: 'Réessayer',
  },
};

const IDIOMAS = Object.keys(GLOBAL_ERROR_T) as Locale[];

function esIdioma(valor: string | null | undefined): valor is Locale {
  return !!valor && (IDIOMAS as string[]).includes(valor);
}

/**
 * Idioma de la página de error. Esta página sustituye al layout raíz (no hay
 * LanguageProvider), así que se resuelve aquí: prefijo de la URL, luego la
 * cookie de idioma, luego el navegador y, si nada sirve, inglés.
 */
function detectLanguage(): Locale {
  try {
    const seg = window.location.pathname.split('/', 2)[1];
    if (esIdioma(seg)) return seg;
  } catch { /* sin window */ }
  try {
    const cookie = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${LOCALE_COOKIE}=`))
      ?.slice(LOCALE_COOKIE.length + 1);
    if (esIdioma(cookie)) return cookie;
  } catch { /* sin document */ }
  try {
    const nav = navigator.language?.split('-')[0]?.toLowerCase();
    if (esIdioma(nav)) return nav;
  } catch { /* sin navigator */ }
  return 'en';
}

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [lang, setLang] = useState<Locale>('en');

  useEffect(() => {
    // Lazy-load posthog to avoid adding it to the global-error chunk
    // (solo en producción: staging y qa no mandan nada a PostHog).
    if (analiticaActivaEnNavegador()) import('posthog-js').then((mod) => {
      mod.default.capture('$exception', {
        $exception_message: error.message,
        $exception_type: error.name,
        $exception_stack_trace_raw: error.stack,
      });
    }).catch(() => { /* analytics not critical for error page */ });
    setLang(detectLanguage());
  }, [error]);

  const t = GLOBAL_ERROR_T[lang] ?? GLOBAL_ERROR_T.en;
  const isConnectionError = error.message?.toLowerCase().includes('tls') ||
    error.message?.toLowerCase().includes('network') ||
    error.message?.toLowerCase().includes('connection');

  return (
    <html lang={lang}>
      <body style={{ margin: 0, fontFamily: 'system-ui, -apple-system, sans-serif', backgroundColor: '#0a0a0a', color: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '400px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>{isConnectionError ? '🔌' : '😵'}</div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            {isConnectionError ? t.connectionTitle : t.errorTitle}
          </h1>
          <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            {isConnectionError ? t.connectionDesc : t.errorDesc}
          </p>
          {isConnectionError && (
            <div style={{ backgroundColor: '#1a1a1a', borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem', textAlign: 'left' }}>
              <p style={{ fontSize: '0.8rem', color: '#888', margin: '0 0 0.5rem 0' }}>{t.tryThese}</p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', color: '#ccc' }}>
                <li style={{ marginBottom: '0.3rem' }}>{t.useBrowser}</li>
                <li style={{ marginBottom: '0.3rem' }}>{t.clearCache}</li>
                <li style={{ marginBottom: '0.3rem' }}>{t.checkInternet}</li>
                <li>{t.updateBrowser}</li>
              </ul>
            </div>
          )}
          <button onClick={reset} style={{ backgroundColor: '#7c3aed', color: '#fff', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer' }}>
            {t.tryAgain}
          </button>
        </div>
      </body>
    </html>
  );
}
